import { forwardRef, useState, type ImgHTMLAttributes } from 'react';
import { ImageOff } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ImageWithFallbackProps extends ImgHTMLAttributes<HTMLImageElement> {
  fallbackClassName?: string;
}

const ImageWithFallback = forwardRef<HTMLImageElement, ImageWithFallbackProps>(
  ({ className, fallbackClassName, src, alt, ...props }, ref) => {
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const handleError = () => {
      setHasError(true);
      setIsLoading(false);
    };

    const handleLoad = () => {
      setIsLoading(false);
    };

    if (hasError || !src) {
      return (
        <div
          className={cn(
            'flex items-center justify-center bg-neutral-100 text-neutral-400',
            className,
            fallbackClassName
          )}
          role="img"
          aria-label={alt || 'Image non disponible'}
        >
          <ImageOff className="h-8 w-8" />
        </div>
      );
    }

    // L'image reste TOUJOURS rendue pendant le chargement, masquee par
    // l'opacite et non par `hidden`.
    //
    // `hidden` pose `display: none`, et une image sans boite de rendu n'est
    // jamais chargee par le navigateur en `loading="lazy"` : elle n'entre
    // jamais "dans le viewport", donc `onLoad` ne se declenche pas, donc
    // `isLoading` reste vrai, donc elle reste masquee. Blocage circulaire :
    // les images differees ne s'affichaient jamais (constate le 2026-09-23 sur
    // la grille du catalogue produits BDisplay, ou seules les premieres
    // cartes — en `loading="eager"` — apparaissaient).
    //
    // Le squelette se superpose en absolu ; le conteneur ne porte aucun style
    // propre hormis `relative`, pour ne pas perturber les mises en page
    // existantes qui dimensionnent l'image via `className`.
    return (
      <span className="relative inline-grid">
        {isLoading && (
          <span
            className={cn('absolute inset-0 animate-pulse rounded-[inherit] bg-neutral-200')}
            aria-hidden="true"
          />
        )}
        <img
          ref={ref}
          src={src}
          alt={alt}
          onError={handleError}
          onLoad={handleLoad}
          className={cn(className, isLoading && 'opacity-0')}
          {...props}
        />
      </span>
    );
  }
);

ImageWithFallback.displayName = 'ImageWithFallback';

export { ImageWithFallback };
